import React, { HTMLAttributes, ReactElement, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import OvSearchField from '../atoms/OvSearchField';
import '../../../styles/autocomplete-custom-styles.scss';

const REQUEST_DELAY_MS = 600;
const MIN_SEARCH_LENGTH = 3;

interface OvSearchTypeaheadProps<T> {
  searchKey: string;
  fetchOptions: (searchText: string) => Promise<T[]> | undefined;
  getOptionLabel: (option: T) => string;
  renderOption: (
    props: HTMLAttributes<HTMLLIElement>,
    option: T
  ) => React.ReactNode;
  onSelect?: (option: T | null) => void;
  placeholder?: string;
  className?: string;
}

const OvSearchTypeahead = <T,>({
  searchKey,
  fetchOptions,
  getOptionLabel,
  renderOption,
  onSelect,
  placeholder,
  className,
}: OvSearchTypeaheadProps<T>): ReactElement => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const updateSearchText = useMemo(
    () => debounce(setSearchText, REQUEST_DELAY_MS),
    []
  );

  const {
    data: options = [],
    isFetching,
    isError,
  } = useQuery({
    queryKey: ['search-typeahead', searchKey, searchText],
    queryFn: async () => (await fetchOptions(searchText)) ?? [],
    enabled: searchText.length >= MIN_SEARCH_LENGTH,
  });

  return (
    <Autocomplete<T, false, false, true>
      className={className}
      freeSolo
      options={options}
      filterOptions={(unfiltered) => unfiltered}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : getOptionLabel(option)
      }
      onInputChange={(_, value) => updateSearchText(value)}
      onChange={(_, value) => {
        if (typeof value !== 'string') {
          onSelect?.(value);
        }
      }}
      renderOption={(props, option) => renderOption(props, option)}
      renderInput={(params) => (
        <OvSearchField
          {...params}
          placeholder={placeholder}
          error={isError}
          helperText={isError ? t('common.listFilters.optionsFailed') : ''}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {params.InputProps.endAdornment}
                <InputAdornment position="end">
                  {isFetching ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : (
                    <SearchIcon fontSize="small" />
                  )}
                </InputAdornment>
              </>
            ),
          }}
          fullWidth
        />
      )}
    />
  );
};

export default OvSearchTypeahead;
